import React, { useState, useEffect } from 'react';
import { isEqual, merge, set, get, cloneDeep, isArray, last } from 'lodash';

const ConfigurationContext = React.createContext(null);

function isBlank(value) {
  return value === '' || value === undefined || value === null;
}

export /**
 * @typedef ConfiguredReportProps
 * @prop {*=} defaultConfiguration
 * @prop {*=} meta
 * @prop {*=} configuration
 * @prop {*=} onChange
 */
/**
 * @type {React.FC<ConfiguredReportProps>}
 */
function ConfiguredReport({
  defaultConfiguration,
  meta,
  configuration,
  onChange = _config => {},
  children,
}) {
  const [
    undoableConfig,
    setUndoableConfig,
    { undo, undoable, redo, redoable },
  ] = useUndoableState(() =>
    merge(cloneDeep(defaultConfiguration), configuration)
  );

  useEffect(() => {
    onChange(undoableConfig);
  }, [undoableConfig, onChange]);

  function wrapInArray(key) {
    // Make sure the key is wrapped in an array. Without this step,
    // setConfig('foo.com', 'bar') would result in { foo: { com: 'bar' } } and
    // not { 'foo.com': bar }
    return [key].flat();
  }

  function getConfig(key = null, defaultValue = null) {
    if (key) {
      const value = get(undoableConfig, wrapInArray(key));
      return isBlank(value) ? defaultValue : value;
    } else {
      return undoableConfig;
    }
  }

  function setConfig(key, value) {
    key = wrapInArray(key);
    let clonedConfig = cloneDeep(undoableConfig);

    // This deals with a problem with `set` where an earlier set call was
    // ['key', '0'] where the value at key was undefined, which gets interpreted
    // as setting up an array. Later it gets called with ['key', 'string'] and
    // now `set` adds a key to an array object. Subsequently `isEqual` ignores
    // the key and the state doesn't update.
    if (
      key.length > 1 &&
      isArray(get(clonedConfig, key.slice(0, -1))) &&
      typeof last(key) !== 'number'
    ) {
      const ary = get(clonedConfig, key.slice(0, -1));
      const obj = { ...ary };
      clonedConfig = set(clonedConfig, key.slice(0, -1), obj);
    }

    return setUndoableConfig(set(clonedConfig, key, value));
  }

  function mergeConfig(newConfig) {
    return setUndoableConfig(merge(cloneDeep(undoableConfig), newConfig));
  }

  function shallowMergeConfig(newConfig) {
    return setUndoableConfig({ ...undoableConfig, ...newConfig });
  }

  function resetConfig(keys) {
    setUndoableConfig(
      merge(
        cloneDeep(undoableConfig),
        wrapInArray(keys).reduce(
          (obj, key) => set(obj, key, get(defaultConfiguration, key, null)),
          {}
        )
      )
    );
  }

  return (
    <ConfigurationContext.Provider
      value={{
        meta,
        get: getConfig,
        set: setConfig,
        merge: mergeConfig,
        shallowMerge: shallowMergeConfig,
        reset: resetConfig,
        undo,
        undoable,
        redo,
        redoable,
      }}
    >
      {children}
    </ConfigurationContext.Provider>
  );
}

export function useReportConfiguration() {
  const context = React.useContext(ConfigurationContext);

  if (!context) {
    throw new Error(
      `useReportConfiguration must be used within a ConfigurationContext.Provider`
    );
  }

  return context;
}

function useUndoableState(initialValue) {
  const [step, setStep] = useState(0);
  const [history, setHistory] = useState(() => [
    typeof initialValue === 'function' ? initialValue() : initialValue,
  ]);

  const totalSteps = history.length - 1;
  const currentValue = history[step];

  const undoSteps = step;
  const undoable = undoSteps > 0;
  const redoSteps = totalSteps - step;
  const redoable = step < totalSteps;

  function setValue(newValue) {
    if (isEqual(newValue, currentValue)) return;

    let newHistory;
    if (step !== totalSteps) {
      newHistory = [...history.slice(0, step + 1), newValue];
    } else {
      newHistory = [...history, newValue];
    }

    setHistory(newHistory);
    setStep(newHistory.length - 1);
  }

  function undo() {
    if (undoSteps > 0 && undoable) {
      setStep(step - 1);
    }
  }

  function redo() {
    if (redoSteps > 0 && redoable) {
      setStep(step + 1);
    }
  }

  return [currentValue, setValue, { undo, redo, undoable, redoable }];
}
